import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { listFeedback, setFeedbackStatus, type FeedbackItem } from '@/services/api/admin';

const PAGE_SIZE = 50;
const STATUSES = ['', 'new', 'open', 'closed'] as const;

function timeAgo(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const STATUS_BADGE: Record<string, string> = {
  new: 'bg-brand-500/15 text-brand-400',
  open: 'bg-amber-500/15 text-amber-400',
  closed: 'bg-ink-muted/15 text-ink-muted',
};

const CATEGORY_BADGE: Record<string, string> = {
  bug: 'bg-rose-500/15 text-rose-400',
  feature: 'bg-violet-500/15 text-violet-400',
  billing: 'bg-emerald-500/15 text-emerald-400',
  security: 'bg-amber-500/15 text-amber-400',
  general: 'bg-ink-muted/15 text-ink-muted',
  other: 'bg-ink-muted/15 text-ink-muted',
};

export function FeedbackPanel() {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>('new');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listFeedback({
        status: status || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [status, offset]);

  useEffect(() => { load(); }, [load]);

  const handleSetStatus = async (id: string, next: 'new' | 'open' | 'closed') => {
    try {
      await setFeedbackStatus(id, next);
      // Optimistic local mutation; if filter excludes the new status, drop the row.
      setItems((prev) => {
        if (status && status !== next) return prev.filter((i) => i.id !== id);
        return prev.map((i) => i.id === id ? { ...i, status: next } : i);
      });
      toast.success(`Marked ${next}`);
    } catch {
      toast.error('Update failed');
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mt-6">
      {/* Filter tabs */}
      <div className="flex gap-1 rounded-clyra-lg border border-line-default bg-clyra-raised p-1 inline-flex">
        {STATUSES.map((s) => (
          <button
            key={s || 'all'}
            type="button"
            onClick={() => { setStatus(s); setOffset(0); }}
            className={`rounded px-3 py-1.5 text-tiny font-medium transition-colors ${
              status === s
                ? 'bg-brand-500/15 text-brand-400'
                : 'text-ink-muted hover:text-ink-secondary'
            }`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="mt-4 space-y-2">
        {loading ? (
          <div className="rounded-clyra-lg border border-line-default px-4 py-12 text-center text-sm text-ink-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-clyra-lg border border-dashed border-line-default px-4 py-12 text-center text-sm text-ink-muted">No feedback in this view.</div>
        ) : items.map((it) => {
          const isOpen = !!expanded[it.id];
          return (
            <div key={it.id} className="rounded-clyra-lg border border-line-default bg-clyra-raised">
              <div className="flex items-start justify-between gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`rounded-full px-2 py-0.5 text-micro font-medium uppercase tracking-wider ${STATUS_BADGE[it.status] ?? STATUS_BADGE.new}`}>{it.status}</span>
                    <span className={`rounded-full px-2 py-0.5 text-micro font-medium uppercase tracking-wider ${CATEGORY_BADGE[it.category] ?? CATEGORY_BADGE.general}`}>{it.category}</span>
                    <span className="text-tiny text-ink-secondary truncate">{it.name}</span>
                    <span className="text-tiny text-ink-faint">·</span>
                    <a href={`mailto:${it.email}`} className="text-tiny text-brand-400 hover:text-brand-500 transition-colors truncate">
                      {it.email}
                    </a>
                    <span className="text-micro text-ink-faint" title={new Date(it.created_at).toLocaleString()}>{timeAgo(it.created_at)}</span>
                  </div>
                  <p className={`mt-2 text-tiny text-ink-secondary whitespace-pre-wrap ${isOpen ? '' : 'line-clamp-2'}`}>{it.message}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <button
                    type="button"
                    onClick={() => setExpanded((m) => ({ ...m, [it.id]: !isOpen }))}
                    className="text-micro text-ink-muted hover:text-ink-primary transition-colors"
                  >
                    {isOpen ? 'Collapse' : 'Expand'}
                  </button>
                  {it.status !== 'open' && (
                    <button onClick={() => handleSetStatus(it.id, 'open')} className="text-micro text-amber-400 hover:text-amber-500 transition-colors">Open</button>
                  )}
                  {it.status !== 'closed' && (
                    <button onClick={() => handleSetStatus(it.id, 'closed')} className="text-micro text-ink-muted hover:text-ink-primary transition-colors">Close</button>
                  )}
                </div>
              </div>
              {isOpen && it.user_id && (
                <div className="border-t border-line-subtle bg-clyra-base/40 px-3 py-2 text-micro text-ink-faint">
                  user: <span className="font-mono text-ink-secondary">{it.user_id}</span>
                  {it.ip && <> · ip: <span className="font-mono text-ink-secondary">{it.ip}</span></>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between text-tiny text-ink-muted">
        <span>{total} item{total === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="rounded px-2 py-1 hover:text-ink-primary disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setOffset(offset + PAGE_SIZE)} className="rounded px-2 py-1 hover:text-ink-primary disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
        </div>
      </div>
    </div>
  );
}
